import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';

import Icon from 'shopper/components/Icon';
import Tag from 'shopper/components/Tag/V2';

import ErrorBoundary from 'components/ErrorBoundary';
import GenericError from 'components/GenericError';

import { sendEvent } from 'lib/gtag';
import placeholder from 'lib/placeholder';

const AdditionalInfos = dynamic(() => import('./AdditionalInfos'));

const StoreInfo = dynamic(() => import('./StoreInfo'));

const TABS_IDS = {
  ADDITIONAL_INFOS: 'additional_info',
  STORE: 'store',
};
const FIXED_TABS = [
  {
    id: TABS_IDS.STORE,
    title: 'Sobre a loja',
    iconName: 'store',
  },
];

const OfferTabs = ({ gaEventCategory, offer }) => {
  // As instruções (`offerInstructions`) agora vivem no card de autor dentro
  // da aba de comentários. Aqui consideramos apenas FAQ e descrição do
  // usuário para decidir se a tab "Informações da promoção" aparece.
  const hasAdditionalInfos =
    offer.offerFaq.length > 0 || Boolean(offer.offerUserDescription);

  const [activeTab, setActiveTab] = useState(() =>
    hasAdditionalInfos ? TABS_IDS.ADDITIONAL_INFOS : TABS_IDS.STORE
  );

  const availableTabsItems = useMemo(() => {
    if (!hasAdditionalInfos) {
      return FIXED_TABS;
    }

    return [
      {
        id: TABS_IDS.ADDITIONAL_INFOS,
        title: 'Informações da promoção',
        iconName: 'info',
      },
      ...FIXED_TABS,
    ];
  }, [hasAdditionalInfos]);

  const onTabClickHandler = (id) => {
    setActiveTab(id);
    sendEvent({
      action: `tab_${id}_clicked`,
      category: gaEventCategory,
    });
  };

  return (
    <div className="mb-6 w-full lg:mb-14">
      <div className="-ml-6 flex w-[calc(100%_+_40px)] snap-x gap-x-2 overflow-x-auto overflow-y-hidden px-6 scrollbar-none lg:-ml-0 lg:w-auto lg:px-0">
        {availableTabsItems.map((tab) => {
          const isActive = activeTab === tab.id;

          return (
            <Tag
              key={tab.id}
              as="button"
              className="flex items-center rounded-5 py-2 text-sm"
              filled={isActive}
              iconLeft={<Icon name={tab.iconName} size="size2" />}
              size="size1"
              type={isActive ? 'primary' : 'neutral'}
              bold
              clickable
              onClick={() => {
                onTabClickHandler(tab.id);
              }}
            >
              {tab.title}
            </Tag>
          );
        })}
      </div>
      <div className="w-full pt-4 lg:pt-6">
        {activeTab === TABS_IDS.ADDITIONAL_INFOS && (
          <ErrorBoundary
            fallback={
              <GenericError
                message={placeholder('errors.couldNotLoadAdditionalInfos')}
              />
            }
          >
            <AdditionalInfos
              offerFaq={offer.offerFaq}
              offerUserDescription={offer.offerUserDescription}
            />
          </ErrorBoundary>
        )}
        {activeTab === TABS_IDS.STORE && (
          <ErrorBoundary
            fallback={
              <GenericError
                message={placeholder('errors.couldNotLoadStoreInfo')}
              />
            }
          >
            <StoreInfo gaEventCategory={gaEventCategory} {...offer} />
          </ErrorBoundary>
        )}
      </div>
    </div>
  );
};

OfferTabs.propTypes = {
  gaEventCategory: PropTypes.string.isRequired,
  offer: PropTypes.shape({
    offerId: PropTypes.number.isRequired,
    offerPhoto: PropTypes.string.isRequired,
    offerPrice: PropTypes.number.isRequired,
    offerPriceType: PropTypes.string.isRequired,
    offerOldPrice: PropTypes.number,
    offerTitle: PropTypes.string.isRequired,
    offerSlug: PropTypes.string.isRequired,
    offerPublished: PropTypes.string.isRequired,
    offerStatusName: PropTypes.string.isRequired,
    offerTags: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
      })
    ),
  }).isRequired,
};

export default OfferTabs;
